import { type RequirementIdentifier, useIntl, useLogout } from '@capsicum/common';
import { ModalService, useInfoModal } from '@capsicum/common-web';
import { useGetDeniedRequirementMessage } from '@capsicum/socios-core';
import styled from 'styled-components';

type UseDeniedRequirementsModalProps = {
	isLogoutModal?: boolean;
	onDismiss?: () => void;
};

export const useDeniedRequirementsModal = ({ isLogoutModal = false, onDismiss }: UseDeniedRequirementsModalProps) => {
	const { $t } = useIntl();
	const logout = useLogout();
	const { showInfoModal } = useInfoModal();

	const { getDeniedRequirementMessage } = useGetDeniedRequirementMessage();

	const showDeniedRequirementModal = (deniedRequirements?: Array<RequirementIdentifier>) => {
		const { description, link, title } = getDeniedRequirementMessage(deniedRequirements);

		const onButtonPress = () =>
			isLogoutModal
				? async () => {
						await logout();
						ModalService.clearModals();
					}
				: close;

		showInfoModal({
			buttonOnHandlers: {
				onPrimaryButtonHandler: onButtonPress,
				primaryInvokeOnDismiss: !isLogoutModal
			},
			buttonTexts: {
				primaryButtonText: isLogoutModal ? $t('global.button.logout') : $t('global.close')
			},
			image: 'warning',
			isClosable: !isLogoutModal,
			onDismiss,
			subtitle: $t<JSX.Element>(description, {
				link: chunks => (
					<S.LinkText data-testid="requirements-denied-modal-link" href={link}>
						{chunks}
					</S.LinkText>
				)
			}),
			testID: 'requirement-denied-reason-modal',
			title,
			variant: 'alert'
		});
	};

	return {
		showDeniedRequirementModal
	};
};

namespace S {
	export const LinkText = styled.a`
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.brand.enabled};
		margin: 0;
	`;
}
